/**
 * Product Gallery Tabs - Switch between Features and Packages view
 * 
 * This script handles the switching between product media images (Features)
 * and variant package images (Packages) in the product gallery.
 */

class ProductGalleryTabs {
  constructor() {
    this.productData = null;
    this.currentTab = 'features';
    this.galleryElement = null;
    // this.thumbnailsElement = null; // Replaced by pageDotsData
    
    this.init();
  }

  init() {
    // Wait for DOM to be ready
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.setup());
    } else {
      this.setup();
    }
  }

  setup() {
    // Get product data from the page
    this.loadProductData();
    
    // Set up event listeners for tab buttons
    this.setupTabButtons();
    
    // Store gallery elements
    this.galleryElement = document.querySelector('.product-gallery__media-list');
    
    // Capture all page-dots elements (thumbnails and mobile dots)
    this.pageDotsData = [];
    document.querySelectorAll('page-dots').forEach(el => {
      this.pageDotsData.push({
        currentElement: el, // Keep track of the current element in DOM
        originalOuterHTML: el.outerHTML, // Store original HTML to restore
        isThumbnail: el.classList.contains('product-gallery__thumbnail-list')
      });
    });

    // Store original gallery HTML for Features tab
    if (this.galleryElement) {
      this.originalGalleryHTML = this.galleryElement.innerHTML;
    }

    // Start watching for variant changes
    this.currentVariantId = this.getVariantIdFromUrl();
    setInterval(() => this.checkVariantChange(), 500);
  }



  getVariantIdFromUrl() {
    const params = new URLSearchParams(window.location.search);
    return params.get('variant');
  }

  checkVariantChange() {
    const newVariantId = this.getVariantIdFromUrl();
    if (newVariantId && newVariantId !== this.currentVariantId) {
      this.currentVariantId = newVariantId;
      this.handleVariantChange(newVariantId);
    }
  }

  handleVariantChange(variantId) {
    // Always switch to packages tab when variant changes
    if (this.currentTab !== 'packages') {
        // Find the button for packages tab
        const packagesBtn = document.querySelector('.product-gallery__tab-btn[data-tab="packages"]');
        if (packagesBtn) {
            this.switchTab('packages', packagesBtn);
        }
    } else {
        // If already on packages tab, just refresh/scroll
        this.renderPackagesGallery(variantId);
    }
  }


  loadProductData() {
    // Try to get product data from meta tag or global variable
    const productMetaTag = document.querySelector('meta[name="product-data"]');
    
    if (productMetaTag) {
      try {
        this.productData = JSON.parse(productMetaTag.content);
      } catch (e) {
        console.error('Failed to parse product data:', e);
      }
    }
    
    // Fallback: try to get from window object
    if (!this.productData && window.productGalleryData) {
      this.productData = window.productGalleryData;
    }
  }

  setupTabButtons() {
    const tabButtons = document.querySelectorAll('.product-gallery__tab-btn');
    
    tabButtons.forEach(button => {
      button.addEventListener('click', (e) => {
        e.preventDefault();
        const tabType = button.dataset.tab;
        this.switchTab(tabType, button);
      });
    });
  }

  switchTab(tabType, clickedButton) {
    if (tabType === this.currentTab) return;
    
    // Update button states
    document.querySelectorAll('.product-gallery__tab-btn').forEach(btn => {
      btn.classList.remove('active');
    });
    clickedButton.classList.add('active');
    
    // Update current tab
    this.currentTab = tabType;
    
    // Render appropriate gallery content
    if (tabType === 'features') {
        this.renderFeaturesGallery(this.currentVariantId);
    } else if (tabType === 'packages') {
        this.renderPackagesGallery(this.currentVariantId);
    }
    
    // Trigger custom event for other components
    document.dispatchEvent(new CustomEvent('gallery:tab-changed', {
      detail: { tab: this.currentTab }
    }));
  }

  renderFeaturesGallery(variantId = null) {
    if (!this.galleryElement || !this.originalGalleryHTML) return;
    
    // Restore original HTML (all product media)
    this.galleryElement.innerHTML = this.originalGalleryHTML;

    // Fix: Clear carousel internal cache and reset state
    if (this.galleryElement._items) {
      this.galleryElement._items = null;
    }
    if (typeof this.galleryElement._selectedIndex !== 'undefined') {
      this.galleryElement._selectedIndex = 0;
    }
    this.galleryElement.style.maxHeight = '';
    
    // Restore all page dots/thumbnails
    this.restorePageDots();

    // Scroll logic
    let scrolled = false;
    if (variantId) {
      const targetImageId = this.getTargetImageId(variantId);
      if (targetImageId && this.galleryElement.querySelector(`[data-media-id="${targetImageId}"]`)) {
        setTimeout(() => this.scrollToImage(targetImageId), 50);
        scrolled = true;
      }
    }

    if (!scrolled) {
      this.galleryElement.scrollLeft = 0;
    }
  }

  renderPackagesGallery(variantId = null) {
    if (!this.galleryElement || !this.productData) return;
    
    // Get unique variant images
    const variantImages = this.getPackagesImages();
    
    if (variantImages.length === 0) return;
    
    // Build HTML for packages gallery (only variant images)
    let html = '';
    variantImages.forEach((image, index) => {
      html += `
        <div class="product-gallery__media snap-center" data-media-type="image" data-media-id="${image.id}">
          <img
            src="${image.src}"
            alt="${image.alt}"
            width="${image.width}"
            height="${image.height}"
            loading="${index === 0 ? 'eager' : 'lazy'}"
            class="object-contain w-full h-full"
            sizes="(max-width: 740px) calc(100vw - 40px), (max-width: 999px) calc(100vw - 64px), min(760px, 42vw)"
          />
        </div>
      `;
    });
    
    // Replace gallery content
    this.galleryElement.innerHTML = html;

    // Fix: Clear carousel internal cache and reset state to prevent max-height: 0px issue
    // because the cached items are now detached from DOM
    if (this.galleryElement._items) {
      this.galleryElement._items = null;
    }
    if (typeof this.galleryElement._selectedIndex !== 'undefined') {
      this.galleryElement._selectedIndex = 0;
    }
    this.galleryElement.style.maxHeight = '';
    
    // Replace thumbnails/dots
    this.updatePageDots(variantImages);
    
    // Scroll to current variant if specified
    let scrolled = false;
    if (variantId) {
      const targetImageId = this.getTargetImageId(variantId);
      if (targetImageId && this.galleryElement.querySelector(`[data-media-id="${targetImageId}"]`)) {
        setTimeout(() => this.scrollToImage(targetImageId), 50);
        scrolled = true;
      }
    }

    if (!scrolled) {
      this.galleryElement.scrollLeft = 0;
    }
  }
  
  restorePageDots() {
    if (!this.pageDotsData) return;
    
    this.pageDotsData.forEach(data => {
        // Create element from original HTML to restore state (including event listeners if they were re-initialized by custom element lifecycle)
        const newEl = this.createElementFromHTML(data.originalOuterHTML);
        
        if (document.body.contains(data.currentElement)) {
          data.currentElement.replaceWith(newEl);
          data.currentElement = newEl;
        } else {
            // Fallback: try to find it by some other means or just skip if it's gone
            // But usually it should be there.
        }
    });
  }

  updatePageDots(variantImages) {
    if (!this.pageDotsData) return;

    this.pageDotsData.forEach(data => {
        // Create a template element from original HTML to preserve attributes
        const templateEl = this.createElementFromHTML(data.originalOuterHTML);
        templateEl.innerHTML = ''; // Clear children
        
        let newInnerHtml = '';
        variantImages.forEach((image, index) => {
             if (data.isThumbnail) {
                 newInnerHtml += this.getThumbnailButtonHtml(image, index);
             } else {
                 newInnerHtml += this.getDotButtonHtml(index);
             }
        });
        
        templateEl.innerHTML = newInnerHtml;
        
        if (document.body.contains(data.currentElement)) {
            data.currentElement.replaceWith(templateEl);
            data.currentElement = templateEl;
        }
    });
  }

  getThumbnailButtonHtml(image, index) {
      // Replicating the thumbnail structure
      // Note: image.src is the full URL. 
      return `
        <button type="button" class="product-gallery__thumbnail" data-media-id="${image.id}" aria-current="${index === 0 ? 'true' : 'false'}" aria-label="Go to item ${index + 1}">
          <img src="${image.src}" class="object-contain rounded-sm" loading="lazy" width="64" height="64" alt="${image.alt || ''}">
        </button>
      `;
  }

  getDotButtonHtml(index) {
      // Replicating the dot structure
      return `
        <button type="button" class="tap-area" aria-current="${index === 0 ? 'true' : 'false'}">
          <span class="sr-only">Go to item ${index + 1}</span>
        </button>
      `;
  }

  createElementFromHTML(htmlString) {
    const div = document.createElement('div');
    div.innerHTML = htmlString.trim();
    return div.firstElementChild;
  }

  getPackagesImages() {
    if (!this.productData || !this.productData.variants) return [];
    
    // Get unique variant images (all of them)
    const variantImages = [];
    const seenSrcs = new Set();
    
    // Create a map of src to media object for fallback lookup
    const srcToMediaMap = new Map();
    if (this.productData.media) {
      this.productData.media.forEach(m => {
        if (m.src) srcToMediaMap.set(m.src, m);
      });
    }

    this.productData.variants.forEach(variant => {
      if (variant.featured_image && variant.featured_image.src) {
        const src = variant.featured_image.src;
        
        // Only add if we haven't seen this image before
        if (!seenSrcs.has(src)) {
          seenSrcs.add(src);
          
          // Try to get Image ID directly, or look it up by src
          let imgId = variant.featured_image.id;
          let width = variant.featured_image.width;
          let height = variant.featured_image.height;
          
          if (srcToMediaMap.has(src)) {
            const m = srcToMediaMap.get(src);
            if (!imgId) imgId = m.id;
            if (!width) width = m.width;
            if (!height) height = m.height;
          }
          
          // If we still don't have an ID, we can't control it via data-media-id
          if (imgId) {
            variantImages.push({
              src: src,
              alt: variant.title || 'Package option',
              id: String(imgId),
              width: width || 1000,
              height: height || 1000
            });
          }
        }
      }
    });
    
    return variantImages;
  }


  getTargetImageId(variantId) {
    if (!this.productData || !variantId) return null;
    
    const variant = this.productData.variants.find(v => v.id == variantId);
    if (variant && variant.featured_image) {
      let targetImageId = variant.featured_image.id;
      
      // Fallback: find by src
      if (!targetImageId && this.productData.media) {
        const match = this.productData.media.find(m => m.src === variant.featured_image.src);
        if (match) targetImageId = match.id;
      }
      
      return targetImageId;
    }
    return null;
  }

  scrollToImage(mediaId) {
    if (!this.galleryElement) return;
    
    const strMediaId = String(mediaId);
    const targetSlide = this.galleryElement.querySelector(`[data-media-id="${strMediaId}"]`);
    
    if (targetSlide) {
      // Scroll to the slide
      targetSlide.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'start' });
      
      // Update thumbnail active state in all page-dots elements
      // We need to find the current elements from our data
      if (this.pageDotsData) {
          this.pageDotsData.forEach(data => {
             const container = data.currentElement;
             if (!container) return;
             
             // Try to find by data-media-id (thumbnails)
             const thumbnails = container.querySelectorAll(`[data-media-id]`);
             if (thumbnails.length > 0) {
                 thumbnails.forEach(thumb => {
                    if (String(thumb.dataset.mediaId) === strMediaId) {
                        thumb.setAttribute('aria-current', 'true');
                    } else {
                        thumb.setAttribute('aria-current', 'false');
                    }
                 });
             } else {
                 // Try to find by index (dots)
                 // But we don't know the index of the mediaId easily without searching the gallery
                 // The gallery items have data-media-id.
                 
                 // Find index of target slide
                 // Note: this.galleryElement._items might be cleared, we need to query DOM
                 const slides = Array.from(this.galleryElement.children);
                 const index = slides.findIndex(slide => String(slide.dataset.mediaId) === strMediaId);
                 
                 if (index !== -1) {
                     const dots = container.querySelectorAll('button');
                     dots.forEach((dot, i) => {
                         dot.setAttribute('aria-current', i === index ? 'true' : 'false');
                     });
                 }
             }
          });
      }
    }
  }
}

// Initialize when script loads
if (typeof window !== 'undefined') {
  window.ProductGalleryTabs = ProductGalleryTabs;
  
  // Auto-initialize
  new ProductGalleryTabs();
}
